import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, from, map, mergeMap, Observable, of, Subscription, switchMap, tap} from 'rxjs';
import {SpinnerService} from 'src/app/utils/services/spinner.service';
import {ItemAvailability, V3Chapter, V3ExerciseSet as ExerciseSet} from '../../model/v3/cspa';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {take} from "rxjs/operators";
import {CspaRestV3} from "../../services/v3/cspa-rest-v3";
import {LogsService} from "../../../utils/services/logs.service";
import {FilterDefinition} from "../../../model/CourseFilters";
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {AuthorizedUserDetailsService, UserRole} from "../../../auth_profile/services/api/authorization.api";
import {CasaNativeApiV3} from "../../../auth_profile/services/api/casa-native-api";

@Component({
    selector: 'exercise-list',
    templateUrl: './exercise-list.component.html',
    styleUrls: ['./exercise-list.component.scss'],
    standalone: false
})
export class ExerciseListComponent implements OnInit, OnDestroy, AfterViewInit {
  availabilitiesMap = new Map<string, ItemAvailability>()
  exerciseSetsMap = new Map<string, ExerciseSet>()
  chaptersMap = new Map<string, V3Chapter>()
  chapters: V3Chapter[]
  chosenCourse: FilterDefinition
  private updateSubscription: Subscription
  private refreshSubscription: Subscription
  private loadSubscription: Subscription
  private refreshDataSubscription: Subscription
  private loadStatusSubscription: Subscription
  status: string
  personId: number

  constructor(
    private provider: NativeServiceApiProvider,
    private auth: AuthorizationServiceProvider,
    private spinner: SpinnerService,
    private logs: LogsService,
    private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.logs.log("loading exercise list screen");

    this.refreshSubscription = this.provider.books().pipe(
      take(1),
      map(api => api.subscribeForRefreshingEvents()),
      switchMap(taskProgressEmitter => taskProgressEmitter),
      switchMap(observable => this.spinner.trace(observable))
    ).subscribe()

    this.updateSubscription = this.provider.books().pipe(
      switchMap(api => api.listenForDataUpdates()),
      switchMap( _ => this.loadData())
    ).subscribe()

    this.loadSubscription = this.loadData().subscribe()

    this.loadStatusSubscription = this.loadStatus().subscribe()
  }

  ngOnDestroy(): void {
    this.updateSubscription?.unsubscribe()
    this.refreshSubscription?.unsubscribe()
    this.loadSubscription?.unsubscribe()
    this.refreshDataSubscription?.unsubscribe()
    this.loadStatusSubscription?.unsubscribe()
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  loadData(){
    return this.spinner.trace(
      this.auth.getAuthDetailsService().pipe(
        switchMap(authDetailsApi => this.loadPersonId(authDetailsApi)),
        switchMap(_ => this.provider.cspaV3()),
        switchMap( api =>
          this.listMappedExerciseSets(api).pipe(
            switchMap( _ => this.listMappedTopAvailabilities(api)),
            mergeMap(set => this.getMappedChapters(api, set))
          )
        )
      )
    )
  }

  private listMappedExerciseSets(api: CspaRestV3): Observable<ExerciseSet[]> {
    return api.listExerciseSets().pipe(
      tap(sets => this.mapExerciseSets(sets))
    )
  }

  private listMappedTopAvailabilities(api: CspaRestV3): Observable<ItemAvailability> {
    return api.listPersonAvailabilities(this.personId, '', 2).pipe(
      tap((availabilities: ItemAvailability[]) =>
        this.mapAvailabilities(availabilities)),
      map(avs => avs.filter(av =>
        !av.path.includes('_') && av.isAssigned && av.isAvailable
      )),
      mergeMap((sets: ItemAvailability[]) => from(sets))
    )
  }

  private getMappedChapters(api: CspaRestV3, set: ItemAvailability) {
    return api.getChapters(`${set.path}_`, 1).pipe(
      map(chapters => {
        this.mapChapters(this.getAvailableChapters(chapters))
        this.filterChapters(this.chosenCourse?.cspaFilter)
      })
    )
  }

  private getAvailableChapters(chapters: V3Chapter[]) {
    return chapters.filter(chapter => {
        const chapterAvail = this.availabilitiesMap.get(chapter.path)
      return chapterAvail && chapterAvail.isAssigned && chapterAvail.isAvailable
      }
    )
  }

  mapExerciseSets(exSets: ExerciseSet[]) {
    exSets.forEach(exSet =>
    this.exerciseSetsMap.set(exSet.path, exSet))
  }

  mapChapters(chapters: V3Chapter[]) {
    chapters.forEach(chapter =>
    this.chaptersMap.set(chapter.path, chapter))
  }

  mapAvailabilities(availabilities: ItemAvailability[]) {
    availabilities.forEach(availability =>
      this.availabilitiesMap.set(availability.path, availability))
  }

  getChapters() {
    return Array.from(this.chaptersMap.keys())
            .sort((aKey, bKey) =>
              this.availabilitiesMap.get(bKey).lastUpdate - this.availabilitiesMap.get(aKey).lastUpdate)
            .map(chapKey =>
              this.chaptersMap.get(chapKey))
  }

  getExerciseSet(chapterPath: string) {
    return this.exerciseSetsMap.get(chapterPath.split('_')[0])
  }

  getAvailability(chapterPath: string) {
    return this.availabilitiesMap.get(chapterPath)
  }

  filterChapters(language: string) {
    if(!language)
      this.chapters = this.getChapters()
    else
    this.chapters = this.getChapters().filter(
      chapter => chapter.path.startsWith(`${language}_`))
    if(this.chapters.length == 0)
      this.chapters = null
  }

  refreshData() {
    this.refreshDataSubscription = this.spinner.trace(
      this.provider.books().pipe(
        switchMap(api => api.refreshData(0))
      )
    ).subscribe()
  }

  getLanguageTranslation(lang: FilterDefinition) {
    return "EBOOKS.FILTER_LANGS." + lang?.translationCode
  }

  setChosenCourse(lang: FilterDefinition) {
    this.chosenCourse = lang
    this.filterChapters(lang?.cspaFilter)
  }

  isStatusActive() {
    return this.status.toUpperCase() == "ACTIVE"
  }

  loadStatus() {
    return this.spinner.trace(this.auth.getAuthDetailsService()
      .pipe(
        switchMap(authDetailsApi =>
          forkJoin({
            role: authDetailsApi.getUserRole(),
            authDetailsApi: this.auth.getAuthDetailsService(),
            casaApi: this.provider.casa()
          })
        ),
        switchMap(({role, authDetailsApi, casaApi }) => {
          if(role == UserRole.Student)
            return this.loadStudentStatus(authDetailsApi, casaApi)
          else if(role == UserRole.Teacher)
            return this.loadTeacherStatus(authDetailsApi, casaApi)
          else
            return of(null)
        })
      )
    );
  }

  private loadPersonId(authDetailsApi: AuthorizedUserDetailsService) {
    return authDetailsApi.getSelfPersonId().pipe(
      tap(personId => this.personId = personId)
    )
  }

  loadStudentStatus(authDetailsApi: AuthorizedUserDetailsService, casaApi: CasaNativeApiV3) {
    return forkJoin([
      authDetailsApi.getSelfSchoolId(),
      authDetailsApi.getSelfStudentId()
    ]).pipe(
      switchMap(([schoolId, studentId]) => casaApi.getStudent(schoolId, studentId)),
      tap(student => this.status = student.status)
    )
  }

  loadTeacherStatus(authDetailsApi: AuthorizedUserDetailsService, casaApi: CasaNativeApiV3) {
    return forkJoin([
      authDetailsApi.getSelfSchoolId(),
      authDetailsApi.getSelfTeacherId()
    ]).pipe(
      switchMap(([schoolId, teacherId]) => casaApi.getTeacher(schoolId, teacherId)),
      tap(teacher => this.status = teacher.status)
    )
  }
}
